import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { jobAPI } from '../../../services';
import { 
  Plus, Search, Filter, Clock, AlertTriangle, CheckCircle,
  XCircle, Eye, Edit2, Users, ChevronDown, Calendar
} from 'lucide-react';
import JobFormModal from './JobFormModal';
import JobDetailModal from './JobDetailModal';

const JobList = () => {
  const { user } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  
  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [editMode, setEditMode] = useState(false);

  const canCreateJob = ['hod', 'admin', 'superadmin'].includes(user?.role);
  const canApprove = ['hr_head', 'admin', 'superadmin'].includes(user?.role);

  useEffect(() => {
    fetchJobs();
  }, [statusFilter, priorityFilter, pagination.page]);

  const fetchJobs = async () => {
    try {
      setLoading(true);
      const response = await jobAPI.getJobs({
        status: statusFilter || undefined,
        priority: priorityFilter || undefined,
        page: pagination.page,
        limit: 20
      });
      
      if (response.status === 'success') {
        setJobs(response.data.jobs);
        setPagination(response.data.pagination);
      }
    } catch (err) {
      setError(err.message || 'Failed to load jobs');
    } finally {
      setLoading(false);
    }
  };

  const handleViewJob = (job) => {
    setSelectedJob(job);
    setEditMode(false);
    setShowDetailModal(true);
  };

  const handleEditJob = (job) => {
    setSelectedJob(job);
    setEditMode(true);
    setShowCreateModal(true);
  };

  const handleJobCreated = () => {
    setShowCreateModal(false);
    setSelectedJob(null);
    fetchJobs();
  };

  const handleJobUpdated = () => {
    setShowDetailModal(false);
    fetchJobs();
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-gray-100 text-gray-700',
      pending: 'bg-yellow-100 text-yellow-700',
      approved: 'bg-green-100 text-green-700',
      rejected: 'bg-red-100 text-red-700',
      on_hold: 'bg-orange-100 text-orange-700',
      closed: 'bg-gray-200 text-gray-600',
      cancelled: 'bg-red-50 text-red-600'
    };
    return styles[status] || 'bg-gray-100 text-gray-700';
  };

  const getPriorityBadge = (priority) => {
    const styles = {
      low: 'bg-blue-50 text-blue-600',
      medium: 'bg-yellow-50 text-yellow-600',
      high: 'bg-orange-50 text-orange-600',
      urgent: 'bg-red-50 text-red-600'
    };
    return styles[priority] || 'bg-gray-50 text-gray-600';
  };

  const getTATStatus = (job) => {
    if (!job.deadline || job.status !== 'approved') return null;
    
    const now = new Date();
    const deadline = new Date(job.deadline);
    const daysLeft = Math.ceil((deadline - now) / (1000 * 60 * 60 * 24));
    
    if (daysLeft < 0) {
      return { label: `${Math.abs(daysLeft)}d overdue`, color: 'text-red-600 bg-red-50' };
    } else if (daysLeft <= 3) {
      return { label: `${daysLeft}d left`, color: 'text-red-600 bg-red-50' };
    } else if (daysLeft <= 7) {
      return { label: `${daysLeft}d left`, color: 'text-yellow-600 bg-yellow-50' };
    }
    return { label: `${daysLeft}d left`, color: 'text-green-600 bg-green-50' };
  };

  const filteredJobs = jobs.filter(job => 
    job.title?.toLowerCase().includes(search.toLowerCase()) ||
    job.department?.toLowerCase().includes(search.toLowerCase()) ||
    job.designation?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Job Requests</h1>
          <p className="text-gray-500 mt-1">
            {pagination.total} total job{pagination.total !== 1 ? 's' : ''}
          </p>
        </div>
        
        {canCreateJob && (
          <button
            onClick={() => {
              setSelectedJob(null);
              setEditMode(false);
              setShowCreateModal(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-5 h-5" />
            Create Job Request
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 bg-white p-4 rounded-xl shadow-sm border border-gray-100">
        <div className="flex-1 min-w-[200px]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search jobs..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>
        
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500"
        >
          <option value="">All Status</option>
          <option value="draft">Draft</option>
          <option value="pending">Pending Approval</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="closed">Closed</option>
        </select>
        
        <select
          value={priorityFilter}
          onChange={(e) => setPriorityFilter(e.target.value)}
          className="px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500"
        >
          <option value="">All Priority</option>
          <option value="low">Low</option>
          <option value="medium">Medium</option>
          <option value="high">High</option>
          <option value="urgent">Urgent</option>
        </select>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {error}
        </div>
      )}

      {/* Loading */}
      {loading ? (
        <div className="flex items-center justify-center min-h-64 bg-white rounded-xl">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : filteredJobs.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-12 text-center">
          <p className="text-gray-500">No jobs found</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredJobs.map(job => {
            const tatStatus = getTATStatus(job);
            return (
              <div 
                key={job._id} 
                className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 hover:shadow-md transition-shadow"
              >
                {/* Header */}
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h3 className="font-semibold text-gray-900 text-lg">{job.title}</h3>
                    <p className="text-sm text-gray-500">{job.department}</p>
                  </div>
                  <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadge(job.status)}`}>
                    {job.status?.toUpperCase().replace('_', ' ')}
                  </span>
                </div>

                {/* Details */}
                <div className="space-y-2 mb-4">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Users className="w-4 h-4" />
                    <span>{job.positions} position{job.positions > 1 ? 's' : ''}</span>
                  </div>
                  
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <span className="font-medium">HOD:</span>
                    <span>{job.requestedBy?.name || 'N/A'}</span>
                  </div>
                  
                  {job.approvedBy && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <span className="font-medium">Approved By:</span>
                      <span>{job.approvedBy?.name}</span>
                    </div>
                  )}
                  
                  {tatStatus && (
                    <div className={`flex items-center gap-2 text-sm px-2 py-1 rounded ${tatStatus.color}`}>
                      <Clock className="w-4 h-4" />
                      <span className="font-medium">{tatStatus.label}</span>
                    </div>
                  )}
                </div>

                {/* Priority & Actions */}
                <div className="flex justify-between items-center pt-3 border-t border-gray-100">
                  <span className={`px-2 py-1 text-xs font-medium rounded ${getPriorityBadge(job.priority)}`}>
                    {job.priority?.toUpperCase()}
                  </span>
                  
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleViewJob(job)}
                      className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="View Details"
                    >
                      <Eye className="w-5 h-5" />
                    </button>
                    
                    {(job.requestedBy?._id === user?._id || user?.role === 'admin') && 
                     ['draft', 'pending', 'rejected'].includes(job.status) && (
                      <button
                        onClick={() => handleEditJob(job)}
                        className="p-2 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                        title="Edit"
                      >
                        <Edit2 className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="flex justify-center gap-2">
          {Array.from({ length: pagination.pages }, (_, i) => i + 1).map(page => (
            <button
              key={page}
              onClick={() => setPagination(prev => ({ ...prev, page }))}
              className={`px-4 py-2 rounded-lg ${
                pagination.page === page
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {page}
            </button>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <JobFormModal
          job={editMode ? selectedJob : null}
          onClose={() => {
            setShowCreateModal(false);
            setSelectedJob(null);
          }}
          onSuccess={handleJobCreated}
        />
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedJob && (
        <JobDetailModal
          job={selectedJob}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedJob(null);
          }}
          onUpdate={handleJobUpdated}
          canApprove={canApprove}
        />
      )}
    </div>
  );
};

export default JobList;
